// A thin wrapper around Google Analytics event tracking
import Merge from 'deepmerge';
import get from 'lodash/get';

import Cookie from '@wetransfer/concorde-cookie';

let firstTime = true;
let timers = {};
let settings = {
  domain: location.hostname,
  name: 'analytics',
  value: '1',
  lazyload: [],
  useDataLayer: true,
};
const dataLayerQueue = [];

export default {
  /**
   * Configure Analytics module options
   * @param  {Object} opts Module options
   */
  configure(opts = {}) {
    settings = Merge(settings, opts);
  },

  /**
   * Enable analytics tracking. We will set a cookie!
   */
  enable() {
    // Set cookie if Analytics was not enabled yet
    // This allow us to remember the user
    if (!this.isEnabled()) {
      Cookie.set(settings.name, settings.value, {
        days: 365,
        domain: settings.domain,
      });

      dataLayerQueue.forEach(this.pushToDataLayer.bind(this));
      dataLayerQueue.length = 0;
    }

    // Process lazyloadable scripts
    for (const loader of settings.lazyload) {
      typeof loader === 'function' && loader();
    }

    // Remove them (just in case .enable is called again)
    settings.lazyload = [];
  },

  /**
   * Make sure the terms/conditions cookie is set.
   * @return {Boolean} True if session has been accepted or T&C have been accepted
   */
  isEnabled() {
    return this.hasAccepted();
  },

  /**
   * Check if terms/conditions cookie is set.
   * @return {Boolean} True if T&C cookie hase been accepted. False in other case.
   */
  hasAccepted() {
    return String(Cookie.get(settings.name)) === settings.value;
  },

  /**
   * check if Google Analytics is available
   * @param  {String}  type Name of the global variable to check
   * @return {Boolean}      True if GA is available. False in other case.
   */
  isAvailable(type = 'dataLayer') {
    if (typeof window[type] === 'undefined') {
      return false;
    }

    const methods = [];
    if (type === 'dataLayer') {
      methods.push('push');
    }

    // Check if certain methods are available
    for (const name of methods) {
      if (typeof window[type][name] === 'undefined') {
        return false;
      }
    }

    return true;
  },

  /**
   * Register a page view event. If it's first page view,
   * GA will send the event on page load
   * @param {String} path Navigation path
   */
  pageView(path) {
    this.recordSnowplowPageView(path);

    if (firstTime) {
      firstTime = false;
      return;
    }

    this.pushToDataLayer({
      event: 'pageview',
      virtualPageTitle: window.document.title,
      virtualPagePath: path,
    });
  },

  /**
   * Push an event to dataLayer
   * @param  {Object} data Data to send
   */
  pushDataLayerEvent(data = {}) {
    this.pushToDataLayer(
      Merge(
        {
          action: undefined,
          label: undefined,
          event: 'event',
          category: undefined,
          virtualPageLocation: undefined,
          virtualPageTitle: undefined,
          virtualPagePath: undefined,
          value: undefined,
          variant: undefined,
          transfer: undefined,
        },
        data
      )
    );
  },

  /**
   * Push some data to dataLayer
   * @param  {Object} data Data to send
   */
  pushToDataLayer(data = {}) {
    if (!this.hasAccepted()) {
      dataLayerQueue.push(data);
      return null;
    }

    if (!this.isAvailable('dataLayer') || !settings.useDataLayer) {
      return null;
    }

    window.dataLayer.push(data);
  },

  /**
   * Start timing an event
   * @param  {String} name Name of the timer to start
   */
  startTimer(name) {
    timers[name] = Date.now();
  },

  /**
   * Stop a timming event
   * @param  {String} name Name of the timer to stop. Must match a start timer name
   * @return {int}         Duration of the timer, in miliseconds
   */
  stopTimer(name) {
    const duration = Date.now() - timers[name];
    delete timers[name];
    return duration;
  },

  /**
   * Get GA clientId property value
   * @return {String} GA clientId value
   */
  get clientId() {
    try {
      const tracker = ga.getAll()[0];
      return tracker.get('clientId');
    } catch (e) {
      return null;
    }
  },

  /**
   * Reset module internal values
   */
  reset() {
    firstTime = true;
    settings = {
      domain: location.hostname,
      name: 'analytics',
      value: '1',
      lazyload: [],
      useDataLayer: true,
    };
    timers = {};
  },

  /**
   * Record a user's first visit to the website (timestamp)
   */
  recordFirstVisitTimestamp(cookieName = 'wt_first_visit') {
    if (!Cookie.get(cookieName)) {
      Cookie.set(cookieName, Date.now(), {
        days: 365,
        domain: settings.domain,
      });
    }
  },

  trackUserConsent() {
    if (!window.__snowplow__) {
      return;
    }

    window.__snowplow__(
      'trackConsentGranted:co',
      '1', // Consent document id
      settings.value, // Consent document version
      settings.name, // Consent document name
      null, // Consent document description
      null, // Expiry of consent
      null, // Custom context for the event
      new Date().getTime() // When the acceptence occurred
    );
  },

  recordSnowplowPageView(path) {
    try {
      window.__snowplow__('trackPageView:co', path, [
        {
          schema: 'iglu:com.wetransfer/launchdarkly/jsonschema/1-0-0',
          data: {
            userKey: get(window, '__feature_flags__.user.key', ''),
          },
        },
      ]);
    } catch (e) {
      return null;
    }
  },
};
